import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ClipboardList, Filter, Plus, Search, X } from "lucide-react";

import { useCreateProject, useProjects } from "@/api/queries";
import type { Project, ProjectStatus } from "@/api/types";
import { Button } from "@/components/Button";
import { Dialog } from "@/components/Dialog";
import { Pagination, TableShell, Td, Th, Tr } from "@/components/DataTable";
import { Field, Input, Select } from "@/components/Input";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/components/toast-context";
import { Page } from "@/layout/Page";
import { PageHeader } from "@/layout/PageHeader";
import { formatDate, formatProjectStatus, pluralize } from "@/lib/format";
import { projectTone } from "@/lib/stock";

const PAGE_SIZE = 50;

const STATUSES: ProjectStatus[] = [
  "PLANNED",
  "ACTIVE",
  "ON_HOLD",
  "COMPLETED",
  "CANCELLED",
];

/**
 * The project list (Frontend Spec §18, PHASE 9).
 *
 * Coverage is deliberately absent from this screen. Working it out per row
 * costs two aggregate queries each, and a list of fifty jobs would issue a
 * hundred of them to fill a column nobody reads before opening the job.
 */
export function ProjectsPage() {
  const [params, setParams] = useSearchParams();
  const [term, setTerm] = useState(params.get("q") ?? "");
  const [debounced, setDebounced] = useState(term);
  const [creating, setCreating] = useState(false);

  const status = params.get("status") ?? undefined;
  const offset = Number(params.get("offset") ?? 0);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(term.trim()), 220);
    return () => window.clearTimeout(timer);
  }, [term]);

  const query = useMemo(
    () => ({
      search: debounced || undefined,
      status: status as ProjectStatus | undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    [debounced, status, offset],
  );

  const { data, isLoading, error, refetch } = useProjects(query);

  function update(next: Record<string, string | undefined>) {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value === undefined || value === "") merged.delete(key);
      else merged.set(key, value);
    }
    if (!("offset" in next)) merged.delete("offset");
    setParams(merged, { replace: true });
  }

  const hasFilters = Boolean(debounced || status);

  return (
    <Page>
      <PageHeader
        title="Projets / Chantiers"
        subtitle="Ce qu'un chantier demande, et ce qui est réellement réservé pour lui."
        actions={
          <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
            <Plus className="size-4" aria-hidden />
            Nouveau projet
          </Button>
        }
      />

      <div className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-surface">
        <div className="flex shrink-0 flex-col gap-2 border-b border-border p-3 sm:flex-row sm:items-center">
          <div className="relative min-w-0 flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
              aria-hidden
            />
            <Input
              value={term}
              onChange={(event) => {
                setTerm(event.target.value);
                update({ q: event.target.value, offset: undefined });
              }}
              placeholder="Nom, code, référence, client…"
              className="pl-9"
              aria-label="Rechercher un projet"
            />
          </div>

          <Select
            value={status ?? ""}
            onChange={(event) => update({ status: event.target.value || undefined })}
            aria-label="Filtrer par statut"
            className="sm:w-52"
          >
            <option value="">Tous les statuts</option>
            {STATUSES.map((value) => (
              <option key={value} value={value}>
                {formatProjectStatus(value)}
              </option>
            ))}
          </Select>

          {hasFilters && (
            <button
              type="button"
              onClick={() => {
                setTerm("");
                setParams(new URLSearchParams(), { replace: true });
              }}
              className="flex h-10 items-center gap-1.5 rounded-md px-2 text-[13px] text-ink-subtle hover:text-ink"
            >
              <X className="size-4" aria-hidden />
              Effacer
            </button>
          )}
        </div>

        <div className="min-w-0">
          {isLoading && <TableSkeleton rows={8} columns={5} />}
          {error && <ErrorState error={error} onRetry={() => void refetch()} />}

          {data && data.items.length === 0 && (
            <EmptyState
              icon={
                hasFilters ? (
                  <Filter className="size-6" aria-hidden />
                ) : (
                  <ClipboardList className="size-6" aria-hidden />
                )
              }
              title={hasFilters ? "Aucun projet ne correspond" : "Aucun projet"}
              description={
                hasFilters
                  ? "Essayez un autre terme, ou retirez les filtres."
                  : "Créez un chantier, ajoutez ce dont il a besoin, et HD-Vault dira ce qui manque vraiment."
              }
            />
          )}

          {data && data.items.length > 0 && (
            <>
              <div className="hidden md:block">
                <TableShell>
                  <thead>
                    <tr>
                      <Th className="w-32 whitespace-nowrap">Code</Th>
                      <Th className="max-w-0">Projet</Th>
                      <Th className="hidden max-w-0 @3xl:table-cell">Client</Th>
                      <Th className="hidden whitespace-nowrap @4xl:table-cell">Début</Th>
                      <Th align="right" className="whitespace-nowrap">
                        Lignes
                      </Th>
                      <Th>Statut</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((project) => (
                      <ProjectRow key={project.id} project={project} />
                    ))}
                  </tbody>
                </TableShell>
              </div>

              <ul className="divide-y divide-border/60 md:hidden">
                {data.items.map((project) => (
                  <li key={project.id}>
                    <ProjectCard project={project} />
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {data && data.total > PAGE_SIZE && (
          <Pagination
            offset={offset}
            limit={PAGE_SIZE}
            total={data.total}
            onChange={(next) => update({ offset: String(next) })}
          />
        )}
      </div>

      <NewProjectDialog open={creating} onOpenChange={setCreating} />
    </Page>
  );
}

function ProjectRow({ project }: { project: Project }) {
  return (
    <Tr>
      <Td className="whitespace-nowrap font-mono text-[12px] text-ink-muted">
        <Link to={`/projects/${project.id}`} className="hover:text-brand">
          {project.code}
        </Link>
      </Td>
      <Td className="max-w-0">
        <Link to={`/projects/${project.id}`} className="block min-w-0 hover:text-brand">
          <span className="block truncate font-medium text-ink">{project.name}</span>
          {project.reference && (
            <span className="block truncate text-[11px] text-ink-subtle">
              Réf. {project.reference}
            </span>
          )}
        </Link>
      </Td>
      <Td className="hidden max-w-0 truncate text-[13px] text-ink-muted @3xl:table-cell">
        {project.client_name ?? "—"}
      </Td>
      <Td className="hidden whitespace-nowrap text-[12px] text-ink-muted @4xl:table-cell">
        {formatDate(project.starts_on)}
      </Td>
      <Td align="right" className="tnum whitespace-nowrap text-[13px] text-ink-muted">
        {project.requirement_count}
      </Td>
      <Td>
        <StatusBadge tone={projectTone(project.status)}>
          {formatProjectStatus(project.status)}
        </StatusBadge>
      </Td>
    </Tr>
  );
}

function ProjectCard({ project }: { project: Project }) {
  return (
    <Link
      to={`/projects/${project.id}`}
      className="flex items-center gap-3 px-3 py-3 active:bg-surface-hover"
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] font-medium text-ink">{project.name}</p>
        <p className="truncate font-mono text-[11px] text-ink-subtle">
          {project.code}
          {project.reference ? ` · ${project.reference}` : ""}
        </p>
        <p className="mt-0.5 truncate text-[12px] text-ink-muted">
          {project.client_name ?? "Sans client"} ·{" "}
          {pluralize(project.requirement_count, "ligne")}
        </p>
      </div>
      <StatusBadge tone={projectTone(project.status)} className="shrink-0">
        {formatProjectStatus(project.status)}
      </StatusBadge>
    </Link>
  );
}

function NewProjectDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const createProject = useCreateProject();
  const [name, setName] = useState("");
  const [reference, setReference] = useState("");
  const [client, setClient] = useState("");
  const [startsOn, setStartsOn] = useState("");

  function reset() {
    setName("");
    setReference("");
    setClient("");
    setStartsOn("");
  }

  async function submit() {
    if (!name.trim()) return;
    try {
      const project = await createProject.mutateAsync({
        name,
        reference,
        client_name: client,
        starts_on: startsOn || null,
        status: "PLANNED",
      });
      toast.success("Projet créé", `${project.code} — ${project.name}`);
      reset();
      onOpenChange(false);
    } catch (error) {
      toast.error("Projet non créé", String(error));
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
      title="Nouveau projet"
      description="HD-Vault attribue le code. Le reste, c'est vos mots."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            disabled={!name.trim() || createProject.isPending}
          >
            {createProject.isPending ? "Création…" : "Créer"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Nom du chantier">
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Rénovation entrepôt"
            aria-label="Nom du projet"
          />
        </Field>
        <Field label="Votre référence" hint="Facultatif. Votre numéro de job, pas le nôtre.">
          <Input
            value={reference}
            onChange={(event) => setReference(event.target.value)}
            aria-label="Référence du projet"
          />
        </Field>
        <Field label="Client" hint="Facultatif.">
          <Input
            value={client}
            onChange={(event) => setClient(event.target.value)}
            aria-label="Client"
          />
        </Field>
        <Field label="Début" hint="Facultatif.">
          <Input
            type="date"
            value={startsOn}
            onChange={(event) => setStartsOn(event.target.value)}
            aria-label="Date de début"
          />
        </Field>
      </div>
    </Dialog>
  );
}
